import cloneDeep from "lodash/cloneDeep";
import { useCallback, useMemo } from "react";

import { IBannerData } from "@faire/retailer/lib/bannerUtils";

export const useBannersData = <T>(
  eligibleBannersDisplayDataInPriorityOrder: IBannerData<T>[]
) => {
  const replacedProductTilesCount: number = useMemo(() => {
    return eligibleBannersDisplayDataInPriorityOrder.reduce((prev, current) => {
      if (current.useStandardPageSize) {
        return prev;
      }
      return prev + current.size;
    }, 0);
  }, [eligibleBannersDisplayDataInPriorityOrder]);

  const sortedEligibleBannersDisplayData: IBannerData<T>[] = useMemo(() => {
    return eligibleBannersDisplayDataInPriorityOrder.sort(
      (displayDataA, displayDataB) =>
        displayDataA.row - displayDataB.row ||
        displayDataA.column - displayDataB.column
    );
  }, [eligibleBannersDisplayDataInPriorityOrder]);

  const shouldRenderBanner = useCallback(
    (
      banner: IBannerData<T>,
      itemsPerRow: number,
      itemLength: number,
      numReplacedProductTiles: number = 0
    ): boolean => {
      if (banner.renderAsModal) {
        return true;
      }
      const bannerPosition =
        itemsPerRow * banner.row - banner.size - numReplacedProductTiles;

      return itemLength >= bannerPosition;
    },
    []
  );

  const displayableBanners = useCallback(
    (itemsPerRow: number, itemLength: number): IBannerData<T>[] => {
      let numReplacedProductTiles = 0;
      const banners = cloneDeep(sortedEligibleBannersDisplayData);
      return banners.filter((displayData) => {
        if (displayData.row <= 0) {
          return false;
        }
        const shouldRender = shouldRenderBanner(
          displayData,
          itemsPerRow,
          itemLength,
          numReplacedProductTiles
        );
        if (shouldRender && !displayData.fullRowBanner) {
          numReplacedProductTiles += displayData.size;
        }
        return shouldRender;
      });
    },
    [shouldRenderBanner, sortedEligibleBannersDisplayData]
  );

  const displayableBannersWithAdjustedRow = useCallback(
    (itemsPerRow: number, itemLength: number): IBannerData<T>[] => {
      const banners = cloneDeep(displayableBanners(itemsPerRow, itemLength));

      let numFullRowBannersAbove = 0;
      banners.forEach((banner) => {
        if (banner.rowNumberRelativeToProductRow) {
          banner.row += numFullRowBannersAbove;
        }

        if (banner.fullRowBanner) {
          numFullRowBannersAbove += 1;
          if (itemsPerRow === 5) {
            banner.size = 5;
          }
        } else {
          // This will fix banner location when the itemPerRow increased to 5
          // in pages with different itemPerRow
          if (banner.column + banner.size - 1 !== itemsPerRow) {
            banner.column = itemsPerRow - banner.size + 1;
          }
        }
      });

      return banners;
    },
    [displayableBanners]
  );

  return useMemo(
    () => ({
      displayableBannersWithAdjustedRow,
      displayableBanners,
      shouldRenderBanner,
      sortedEligibleBannersDisplayData,
      replacedProductTilesCount,
    }),
    [
      displayableBannersWithAdjustedRow,
      displayableBanners,
      shouldRenderBanner,
      sortedEligibleBannersDisplayData,
      replacedProductTilesCount,
    ]
  );
};
